/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-restricted-syntax */
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ky from 'ky';
import { PromptCollection, Prompt } from '../@types';
import * as Constants from '../constants';
import { api, ApiResponse, throwError } from './BaseApi';

export const cache = {
  SECTION_PROMPTS: 'sectionPrompts',
  PROMPT: 'prompt',
  PROMPT_COMMENTS: 'promptComments',
};
export { cache as PROMPT_CACHE };

const formatPromptsByTimestamp = (prompts: Prompt[]) => {
  // episode.sections.forEach((section: any, i: number) => {
  //   const { prompts } = section;
  const promptsByTimestamp: PromptCollection = {};
  if (prompts && prompts.length) {
    prompts.forEach((prompt: any) => {
      promptsByTimestamp[prompt.timestamp] = prompt;
    });
  }
  return promptsByTimestamp;
};

export const getPromptsBySectionId = async (sectionId: string) => {
  // const response = await api.get('users/123');
  const prompts: any = await api.get(`prompts/${sectionId}`).json();
  return formatPromptsByTimestamp(prompts);
};

export const getPromptById = async (promptId: string) => {
  // const response = await api.get('users/123');
  const prompt: any = await api.get(`prompt/${promptId}`).json();
  return { prompt };
};

const actions = {
  promptLike: async (promptId: string, userId: string) => {
    // Replace with API Call
    const { data: prompt }: any = usePrompt(promptId);
    if (!prompt) {
      return null;
    }
    prompt.interactions.liked = !prompt.interactions.liked;
    if (prompt.counts) {
      prompt.counts.LIKES = prompt.interactions.liked
        ? prompt.counts.LIKES + 1
        : prompt.counts.LIKES - 1;
    }
    return prompt;
  },
  addPromptComment: async ({ prompt_id, user_id, comment }: any) => {
    // Replace with API Call
    const { data: prompt }: any = usePrompt(prompt_id);

    if (prompt) {
      prompt.comments = prompt.comments
        ? prompt.comments.concat([comment])
        : [comment];
      if (prompt.counts) {
        prompt.counts.COMMENTS += 1;
      }
      return prompt;
    }
    return null;
  },
};

export function usePrompts(sectionId: string) {
  return useQuery(
    [cache.SECTION_PROMPTS, sectionId],
    () => getPromptsBySectionId(sectionId),
    {
      staleTime: 10000,
    }
  );
}

export function usePrompt(promptId: string) {
  return useQuery([cache.PROMPT, promptId], () => getPromptById(promptId));
}

const togglePromptLike = async ({ prompt_id, user_id }: any) => {
  // add try catch
  const prompt = await actions.promptLike(prompt_id, user_id);
  return prompt;
};

export const usePromptLike = () => {
  const queryClient = useQueryClient();
  return useMutation(togglePromptLike, {
    onSuccess: (prompt) => {
      queryClient.setQueryData([cache.PROMPT, prompt.id], prompt);
    },
  });
};

const getCommentsByPromptId = async (
  promptId: string,
  sonarGroupId: string
) => {
  try {
    const res: any = await api.post(`prompt/${promptId}/load-comments`, {
      json: {
        sonar_group_id: sonarGroupId,
      },
    });
    const { response, error } = await ApiResponse(res);
    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem fetching comments.');
  }
};

export function usePromptComments(promptId: string, sonarGroupId: string) {
  return useQuery(
    [cache.PROMPT_COMMENTS, promptId, sonarGroupId],
    () => getCommentsByPromptId(promptId, sonarGroupId),
    {
      keepPreviousData: true,
      enabled: !!promptId && !!sonarGroupId,
    }
  );
}

const addPromptComment = async (props: any) => {
  const { prompt_id, user_id, sonar_group_id, comment } = props;
  try {
    const res: any = await api.post(`prompt/${prompt_id}/comments`, {
      json: {
        sonar_group_id,
        user_id,
        comment,
      },
    });

    const { response, error } = await ApiResponse(res);

    if (error) throw error;
    return response;
  } catch (err) {
    return throwError(err, 'There was a problem submitting your comment.');
  }
};

export const useAddPromptComment = () => {
  const queryClient = useQueryClient();
  return useMutation(addPromptComment, {
    onSuccess: (res) => {
      queryClient.refetchQueries([
        cache.PROMPT_COMMENTS,
        res.prompt_id,
        res.sonar_group_id,
      ]);
    },
  });
};
